import { toast } from 'react-toastify';

import { createDefaultHeaders } from '../api';
import { getSession, urlWithAdminQuery } from '../auth';
import { ServerDevice } from './types';

// const API_URL = process.env.REACT_APP_API_URL

export const edit = (editObj: ServerDevice) => {
  return getSession().then((session) =>
    fetch(urlWithAdminQuery(`/device/${editObj.id}`, session.adminEmail), {
      method: 'PUT',
      headers: createDefaultHeaders({
        authToken: session.idToken.toString(),
      }),
      body: JSON.stringify({
        ix: editObj.ix,
        name: editObj.name,
        leak_enabled: editObj.leak_enabled,
        desired_usage_alert_enabled: editObj.desired_usage_alert_enabled,
        leak_threshold_hours: Number(editObj.leak_threshold_hours),
        desired_usage_alert_threshold_gallons: Number(
          editObj.desired_usage_alert_threshold_gallons,
        ),
        desired_usage_alert_window_seconds: Number(editObj.desired_usage_alert_window_seconds),
      }),
    })
      .then(async (resp) => {
        if (!resp.ok) {
          let errorMessage = 'Unknown error';
          try {
            errorMessage = await resp.text();
          } catch {
            errorMessage = resp.statusText || 'Unknown error';
          }

          // Check for permission errors
          if (errorMessage.includes('MANAGE_DEVICES')) {
            toast.error(
              "You don't have permission to edit device settings. Please contact your account owner.",
              { closeButton: true, autoClose: 5000 },
            );
          } else if (errorMessage.includes('Missing required permissions')) {
            toast.error(
              "You don't have permission to perform this action. Please contact your account owner.",
              { closeButton: true, autoClose: 5000 },
            );
          } else {
            toast.error(`Failed to edit device: ${errorMessage}`, { closeButton: true });
          }
          throw new Error(errorMessage);
        }
        return resp.json();
      })
      .catch((err) => {
        console.error(err);
        // Only show generic error if we haven't already shown a specific one
        if (
          !err.message ||
          (!err.message.includes('MANAGE_DEVICES') &&
            !err.message.includes('Missing required permissions'))
        ) {
          toast.error(`Error editing device ${editObj.id}: ${err.message || 'Unknown error'}`, {
            closeButton: true,
          });
        }
        throw err;
      }),
  );
};

/** Update multiple devices at once */
export const batchEdit = (
  deviceIds: string[],
  leakProperties: {
    leak_enabled?: boolean;
    leak_threshold_hours?: number;
    leak_flow_threshold_gallons?: number;
    desired_usage_alert_window_seconds?: number;
    desired_usage_alert_threshold_gallons?: number;
  },
): Promise<ServerDevice[]> => {
  return getSession().then((session) =>
    fetch(urlWithAdminQuery(`/device/batchUpdate`, session.adminEmail), {
      method: 'PUT',
      headers: createDefaultHeaders({
        authToken: session.idToken.toString(),
      }),
      body: JSON.stringify({
        device_ids: deviceIds,
        ...leakProperties,
      }),
    })
      .then(async (resp) => {
        if (!resp.ok) {
          let errorMessage = 'Unknown error';
          try {
            errorMessage = await resp.text();
          } catch {
            errorMessage = resp.statusText || 'Unknown error';
          }

          // Check for permission errors
          if (errorMessage.includes('MANAGE_DEVICES')) {
            toast.error(
              "You don't have permission to edit device settings. Please contact your account owner.",
              { closeButton: true, autoClose: 5000 },
            );
          } else if (errorMessage.includes('Missing required permissions')) {
            toast.error(
              "You don't have permission to perform this action. Please contact your account owner.",
              { closeButton: true, autoClose: 5000 },
            );
          } else {
            toast.error(`Failed to update devices: ${errorMessage}`, { closeButton: true });
          }
          throw new Error(errorMessage);
        }
        return resp.json();
      })
      .catch((err) => {
        console.error(err);
        // Only show generic error if we haven't already shown a specific one
        if (
          !err.message ||
          (!err.message.includes('MANAGE_DEVICES') &&
            !err.message.includes('Missing required permissions'))
        ) {
          toast.error(`Error updating multiple devices: ${err.message || 'Unknown error'}`, {
            closeButton: true,
          });
        }
        throw err;
      }),
  );
};
